import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { Mail, ShieldCheck } from "lucide-react";
import {
  extractTeacherRecord,
  extractTeacherToken,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  clearTeacherSignupContext,
  setTeacherSession,
} from "../../features/teacher/teacherSlice";
import {
  TeacherAuthField,
  TeacherAuthLayout,
  TeacherFeedbackMessage,
} from "./TeacherUI";
import { getTeacherAuthErrorMessage } from "../../utils/uiErrorMessages";

export default function TeacherOtpVerification() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const signupContext = useSelector((state) => state.teacher?.signupContext);
  const [formData, setFormData] = useState({
    pre_auth_token:
      location.state?.preAuthToken || signupContext?.preAuthToken || "",
    otp: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalMessage, setGeneralMessage] = useState("");
  const source = String(
    location.state?.source || signupContext?.source || "signup"
  )
    .trim()
    .toLowerCase();
  const isLoginOtp = source === "login";
  const redirectPath = location.state?.redirectPath || "/teacherdashboard";
  const entryMessage = String(location.state?.message || "").trim();

  const email = useMemo(
    () => String(location.state?.email || signupContext?.email || "").trim(),
    [location.state, signupContext]
  );

  const otpMutation = useMutation({
    mutationFn: isLoginOtp ? teacherApi.verifyLoginOtp : teacherApi.verifyOtp,
    onSuccess: (payload) => {
      const teacherToken = extractTeacherToken(payload);
      const teacherRecord = extractTeacherRecord(payload, {
        email,
      });

      if (!teacherToken) {
        setGeneralMessage(
          "OTP verification succeeded but the backend did not return a teacher JWT."
        );
        return;
      }

      dispatch(
        setTeacherSession({
          token: teacherToken,
          teacher: teacherRecord,
        })
      );
      dispatch(clearTeacherSignupContext());
      navigate(redirectPath, { replace: true });
    },
    onError: (error) => {
      setFieldErrors(error.fieldErrors || {});
      setGeneralMessage(getTeacherAuthErrorMessage(error, "otp"));
    },
  });

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (fieldErrors[name]) {
      setFieldErrors((previous) => ({
        ...previous,
        [name]: "",
      }));
    }

    if (generalMessage) {
      setGeneralMessage("");
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFieldErrors({});
    setGeneralMessage("");

    const nextErrors = {};

    if (!formData.pre_auth_token.trim()) {
      nextErrors.pre_auth_token = "pre_auth_token is required.";
    }

    if (!formData.otp.trim()) {
      nextErrors.otp = "OTP is required.";
    }

    if (Object.keys(nextErrors).length) {
      setFieldErrors(nextErrors);
      return;
    }

    otpMutation.mutate({
      pre_auth_token: formData.pre_auth_token.trim(),
      otp: formData.otp.trim(),
    });
  };

  return (
    <TeacherAuthLayout
      title={isLoginOtp ? "Verify Teacher Login OTP" : "Verify OTP"}
      subtitle={
        isLoginOtp
          ? "Enter the OTP sent by the backend to finish teacher login."
          : "Enter the OTP sent by the backend to finish teacher signup."
      }
      closeTo={isLoginOtp ? "/teacherlogin-otp" : "/teachersignup"}
      footer={
        <p className="teacher-auth-footer-text">
          Need to start over?{" "}
          <Link
            className="teacher-auth-link"
            to={isLoginOtp ? "/teacherlogin-otp" : "/teachersignup"}
          >
            {isLoginOtp ? "Back to OTP login" : "Back to signup"}
          </Link>
        </p>
      }
    >
      {email ? (
        <TeacherFeedbackMessage variant="info">
          OTP verification is ready for <strong>{email}</strong>.
        </TeacherFeedbackMessage>
      ) : null}
      {entryMessage ? (
        <TeacherFeedbackMessage variant="info">{entryMessage}</TeacherFeedbackMessage>
      ) : null}
      {generalMessage ? (
        <TeacherFeedbackMessage variant="error">{generalMessage}</TeacherFeedbackMessage>
      ) : null}

      <form className="teacher-auth-form" onSubmit={handleSubmit} noValidate>
        <input
          type="hidden"
          name="pre_auth_token"
          value={formData.pre_auth_token}
        />

        <TeacherAuthField
          id="teacher-otp"
          label="OTP"
          icon={ShieldCheck}
          name="otp"
          type="text"
          value={formData.otp}
          onChange={handleChange}
          placeholder="Enter the OTP"
          error={fieldErrors.otp}
        />

        <TeacherAuthField
          id="teacher-otp-email"
          label="Signup Email"
          icon={Mail}
          name="signup_email"
          type="text"
          value={email}
          readOnly
          note="Displayed for confirmation only."
        />

        <button className="teacher-auth-button" type="submit" disabled={otpMutation.isPending}>
          {otpMutation.isPending ? "Verifying OTP..." : "Verify and Continue"}
        </button>
      </form>
    </TeacherAuthLayout>
  );
}
